import { KindeUser } from "@inrange/building-manager-api-client/models-organisation";
import { useState } from "react";
import styled from "styled-components";
import SimpleButton from "../buttons/SimpleButton";

const AddEmailUser = ({
  onClick,
  addButtonDisabled,
  currentUsers,
}: {
  onClick: (user: {
    firstName: string;
    lastName: string;
    email: string;
  }) => void;
  addButtonDisabled: boolean;
  currentUsers: KindeUser[];
}) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");

  const validateEmail = (newEmail: string) => {
    if (!newEmail) return "Please fill in this field.";
    const emailPattern = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!emailPattern.test(newEmail))
      return "Please provide a valid email address.";
    if (currentUsers.some((u) => u.email === newEmail))
      return "User already exists";
    return "";
  };

  const validateFirstName = () => {
    if (!firstName.trim()) {
      return "First name is required";
    }
    return "";
  };

  const validateLastName = () => {
    if (!lastName.trim()) {
      return "Last name is required";
    }
    return "";
  };

  const handleAddUser = () => {
    const emailErr = validateEmail(email);
    if (emailErr) {
      setEmailError(emailErr);
    }
    const firstNameErr = validateFirstName();
    if (firstNameErr) {
      setFirstNameError(firstNameErr);
    }
    const lastNameErr = validateLastName();
    if (lastNameErr) {
      setLastNameError(lastNameErr);
    }

    if (emailErr || firstNameErr || lastNameErr) return;

    onClick({ firstName, lastName, email });
    setFirstName("");
    setLastName("");
    setEmail("");
    setEmailError("");
    setFirstNameError("");
    setLastNameError("");
  };

  return (
    <FlexColumn>
      <FlexRow
        style={{ justifyContent: "space-between", alignItems: "flex-start" }}
      >
        <FlexColumn style={{ flexGrow: 1 }}>
          <Input
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
              setFirstNameError("");
            }}
            placeholder="First Name"
            required
            data-testid="user-firstname-input"
          />
          {firstNameError && <Error>{firstNameError}</Error>}
        </FlexColumn>
        <FlexColumn style={{ flexGrow: 1 }}>
          <Input
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value);
              setLastNameError("");
            }}
            placeholder="Last Name"
            required
            data-testid="user-lastname-input"
          />
          {lastNameError && <Error>{lastNameError}</Error>}
        </FlexColumn>
      </FlexRow>
      <FlexRow
        style={{ justifyContent: "space-between", alignItems: "flex-start" }}
      >
        <FlexColumn style={{ flexGrow: 1 }}>
          <Input
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setEmail(e.target.value);
              setEmailError("");
              e.target.classList.remove("error");
            }}
            placeholder="Email"
            type="email"
            pattern="^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$"
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
              const error = validateEmail(e.target.value);
              setEmailError(error);
              e.target.classList.toggle("error", !!error);
            }}
            id="email-input"
            required
            data-testid="user-email-input"
          />
          {emailError && <Error>{emailError}</Error>}
        </FlexColumn>
        <SimpleButton
          height="40px"
          label="Add user"
          onClick={handleAddUser}
          disabled={
            !!emailError ||
            !!firstNameError ||
            !!lastNameError ||
            addButtonDisabled
          }
          data-testid="add-user-button"
        />
      </FlexRow>
    </FlexColumn>
  );
};

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: start;
`;

const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const Input = styled.input`
  padding: 4px 8px;
  height: 40px;
  background: #ffffff;
  border: 1px solid #bbc1d3;
  border-radius: 4px;
  flex-grow: 1;

  &:focus {
    outline: 1px solid #00022f;
    border: none;
  }

  ::placeholder {
    color: rgba(0, 0, 0, 0.2);
  }

  &.error {
    border-color: #f04438;
    border: 1px solid #f04438;
  }
`;

export const Error = styled.div`
  font-size: 14px;
  padding-left: 5px;
  font-style: normal;
  color: #f04438;
`;

export default AddEmailUser;
